// Full-screen "Voice Mode" overlay for the AI Coach.
//
// Conversation loop:
//   listening → user speaks → silence triggers send
//   thinking  → waiting for the AI reply text
//   speaking  → TTS reads the reply aloud
//   back to listening (auto)
//
// Tap anywhere to interrupt the current state — the standard "barge-in"
// gesture. Stops TTS / cancels listening and re-opens the mic.
//
// Entry / exit are driven by HayahAICoach: it owns send() + the message
// stream and passes us a sendVoice(text) callback. We don't reach into
// chat state directly.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { startDictation, sanitizeError, isPermissionError, openIOSSettings } from "./dictate";
import { hapticImpact } from "./haptics";

// Lazy import the TTS plugin — only loaded when Voice Mode is opened.
async function loadTTS() {
  try {
    const m = await import("@capacitor-community/text-to-speech");
    return m.TextToSpeech;
  } catch { return null; }
}

// Strip markdown / emoji so the TTS reads clean prose, not symbols
// ("asterisk asterisk bold"). Keeps link text, drops the URL + syntax.
function toSpeech(text) {
  return String(text || "")
    .replace(/```[\s\S]*?```/g, " ")           // fenced code
    .replace(/`([^`]+)`/g, "$1")               // inline code
    .replace(/!?\[([^\]]+)\]\([^)]+\)/g, "$1") // links/images → text
    .replace(/^\s*#{1,6}\s+/gm, "")            // headings
    .replace(/^\s*>\s?/gm, "")                 // blockquotes
    .replace(/^\s*[-*•]\s+/gm, "")             // list bullets
    .replace(/\*\*([^*]+)\*\*/g, "$1")         // bold
    .replace(/(\*|_)([^*_]+)\1/g, "$2")        // italic
    .replace(/\|/g, " ")                       // table pipes
    .replace(/[#*_`>]/g, "")                   // stray markers
    .replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}]/gu, "") // emoji/symbols
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();
}
// Split into speakable chunks at sentence boundaries, merging fragments that
// are too short so the TTS doesn't stutter on "Yes." "OK." etc.
function splitSentences(text) {
  const raw = String(text || "").split(/(?<=[.!?…])\s+|\n+/).map(s => s.trim()).filter(Boolean);
  const out = [];
  for (const s of raw) {
    if (out.length && (out[out.length - 1].length < 24 || s.length < 24)) out[out.length - 1] += " " + s;
    else out.push(s);
  }
  return out.length ? out : (text ? [String(text)] : []);
}

const C = {
  bg:    "#07090c",
  ink:   "#f4eee0",
  dim:   "rgba(244,238,224,0.62)",
  faint: "rgba(244,238,224,0.45)",
  amber: "#e0a030",
  coral: "#f08a6b",
  green: "#7CC4A0",
};

export function VoiceMode({ open, onClose, onSend, latestReply, replyPending, lang }) {
  // Phase machine. "idle" before first mic open / after a stop;
  // "listening" while mic is active; "thinking" after send + before reply;
  // "speaking" while TTS plays.
  const [phase, setPhase] = useState("idle");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);
  const [micLevel, setMicLevel] = useState(0);

  const sessionRef = useRef(null);
  const baseRef = useRef("");
  const ttsRef = useRef(null);
  // Track which reply text we've already spoken so a stale render doesn't
  // re-trigger TTS after we've already moved on.
  const spokenRef = useRef(null);
  // Mounted guard for async chain after unmount.
  const mountedRef = useRef(false);
  // Set true to abort an in-flight sentence queue (interrupt / unmount).
  const speakCancelRef = useRef(false);

  useEffect(() => { mountedRef.current = open; return () => { mountedRef.current = false; speakCancelRef.current = true; }; }, [open]);

  // Cleanup on close
  useEffect(() => {
    if (open) return;
    if (sessionRef.current) { try { sessionRef.current.stop?.(); } catch {} sessionRef.current = null; }
    (async () => {
      try { const TTS = await loadTTS(); await TTS?.stop?.(); } catch {}
    })();
    setPhase("idle"); setTranscript(""); setError(null); setMicLevel(0);
    spokenRef.current = null;
  }, [open]);

  // Open → kick off the loop with a listening session
  useEffect(() => {
    if (!open) return;
    startListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Whenever a new finalized reply arrives, speak it then re-listen
  useEffect(() => {
    if (!open) return;
    if (!latestReply) return;
    if (replyPending) return;             // still streaming
    if (spokenRef.current === latestReply) return;
    spokenRef.current = latestReply;
    speakAndContinue(latestReply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, latestReply, replyPending]);

  async function startListening() {
    if (!mountedRef.current) return;
    setPhase("listening");
    setTranscript("");
    setError(null);
    setMicLevel(0);
    baseRef.current = "";
    try { hapticImpact("light"); } catch {}
    try {
      const session = await startDictation({
        lang,
        onPartial: (live) => { baseRef.current = live; setTranscript(live); },
        onFinal: (final) => { baseRef.current = final; setTranscript(final); },
        onAudioLevel: (lvl) => setMicLevel(lvl),
        onError: (msg) => {
          if (!mountedRef.current) return;
          setError(sanitizeError(msg));
          setPhase("idle");
        },
        onEnd: (reason) => {
          if (!mountedRef.current) return;
          // "speech" reason means iOS detected end-of-utterance.
          // Forward to chat — we always auto-send in voice mode, regardless
          // of the user's review/auto-send pref.
          if (reason === "speech") {
            const text = (baseRef.current || "").trim();
            if (text) {
              setPhase("thinking");
              try { hapticImpact("medium"); } catch {}
              onSend?.(text);
            } else {
              // No content; re-arm.
              startListening();
            }
            return;
          }
          // user / error / giveup → go idle.
          if (reason !== "user") setPhase("idle");
        },
      });
      sessionRef.current = session;
    } catch (e) {
      setError("Couldn't start voice mode.");
      setPhase("idle");
    }
  }

  // Voice-barge-in watcher: while TTS plays we open a side dictation session
  // purely as an audio-level monitor. If sustained mic energy crosses the
  // threshold (user starts speaking over the assistant), we interrupt TTS
  // and pivot to a real listening turn. Note: iOS's SFSpeechRecognizer
  // session also captures speaker output, so the threshold is set above
  // typical playback bleed — works best with the device speaker pointed
  // away from the user (i.e. earpiece/headphones gives best barge-in).
  const bargeInSustainStartRef = useRef(0);

  async function startBargeInWatcher() {
    if (!mountedRef.current) return null;
    try {
      const session = await startDictation({
        lang,
        onAudioLevel: (lvl) => {
          if (!mountedRef.current) return;
          // Threshold tuned ~5x typical TTS playback bleed from device speaker.
          const THRESHOLD = 0.32;
          const SUSTAIN_MS = 220;
          if (lvl >= THRESHOLD) {
            const now = Date.now();
            if (bargeInSustainStartRef.current === 0) bargeInSustainStartRef.current = now;
            if (now - bargeInSustainStartRef.current >= SUSTAIN_MS) {
              // Sustained voice over TTS — pull the trigger.
              bargeInSustainStartRef.current = 0;
              handleInterrupt();
            }
          } else {
            bargeInSustainStartRef.current = 0;
          }
        },
        onPartial: () => {},
        onFinal: () => {},
        onError: () => {},
        onEnd: () => {},
      });
      return session;
    } catch { return null; }
  }

  async function speakAndContinue(text) {
    if (!mountedRef.current) return;
    setPhase("speaking");
    setError(null);
    speakCancelRef.current = false;
    try { hapticImpact("light"); } catch {}
    // Spin up the barge-in watcher in parallel with TTS playback.
    const watcher = await startBargeInWatcher();
    try {
      const TTS = await loadTTS();
      ttsRef.current = TTS;
      if (!TTS) {
        // No TTS available — tell the user instead of going silently dead.
        if (mountedRef.current) setError("Voice playback isn't available on this device.");
        if (watcher) { try { await watcher.stop(); } catch {} }
        if (mountedRef.current) startListening();
        return;
      }
      await TTS.stop?.();
      // Speak sentence-by-sentence so the FIRST line starts almost instantly
      // (ChatGPT-voice feel) and an interrupt can cut in between sentences.
      const sentences = splitSentences(toSpeech(text));
      let spokeAny = false;
      for (const sentence of sentences) {
        if (!mountedRef.current || speakCancelRef.current) break;
        try {
          await TTS.speak({ text: sentence.slice(0, 1000), lang: lang || "en-US", rate: 1.0, pitch: 1.0, volume: 1.0, category: "playback" });
          spokeAny = true;
        } catch (e) {
          if (mountedRef.current && !speakCancelRef.current) setError("Couldn't play audio — tap the orb to retry.");
          break;
        }
      }
      if (!spokeAny && mountedRef.current && !speakCancelRef.current && sentences.length) {
        setError("Couldn't play audio — tap the orb to retry.");
      }
    } catch (e) {
      if (mountedRef.current && !speakCancelRef.current) setError("Couldn't play audio — tap the orb to retry.");
    } finally {
      // Tear down the watcher and re-arm a normal listening session.
      if (watcher) { try { await watcher.stop(); } catch {} }
      bargeInSustainStartRef.current = 0;
      if (mountedRef.current) startListening();
    }
  }

  async function handleInterrupt() {
    try { hapticImpact("light"); } catch {}
    speakCancelRef.current = true; // abort any remaining queued sentences
    // Stop TTS if speaking
    try { const TTS = await loadTTS(); await TTS?.stop?.(); } catch {}
    // Stop dictation if listening
    if (sessionRef.current) {
      try { await sessionRef.current.stop(); } catch {}
      sessionRef.current = null;
    }
    // Restart listening for a fresh turn
    if (mountedRef.current) startListening();
  }

  if (!open) return null;

  const phaseLabel = {
    idle:      "Idle",
    listening: "Listening",
    thinking:  "Thinking",
    speaking:  "Speaking",
  }[phase] || phase;

  const phaseColor = {
    listening: C.coral,
    thinking:  C.amber,
    speaking:  C.green,
    idle:      C.dim,
  }[phase] || C.dim;

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 100000,
      background: C.bg, color: C.ink,
      fontFamily: "ui-rounded, 'SF Pro Rounded', -apple-system, sans-serif",
      display: "flex", flexDirection: "column",
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>
      <style>{`
        @keyframes vm-pulse{0%,100%{transform:scale(1);opacity:0.85}50%{transform:scale(1.05);opacity:1}}
        @keyframes vm-rotate{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes vm-sweep{0%{background-position:0% 50%}100%{background-position:200% 50%}}
        .vm-content-scroll::-webkit-scrollbar{display:none}
        .vm-content-scroll{scrollbar-width:none}
      `}</style>

      {/* Header — exit button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px" }}>
        <div style={{ fontSize: 11, color: C.faint, letterSpacing: "0.2em", textTransform: "uppercase" }}>Voice Mode</div>
        <button
          onClick={onClose}
          aria-label="Exit voice mode"
          style={{
            padding: "7px 12px", borderRadius: 14,
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)",
            color: C.ink, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Done
        </button>
      </div>

      {/* Orb sits in a fixed-height row up top; the reply/transcript area
          below grows to fill the rest of the screen and scrolls when the
          coach answer is long. Previously the response was truncated at
          200 chars + visually clipped to a ~80px tray. */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 24px 0", gap: 20, minHeight: 0 }}>
        <button
          onClick={handleInterrupt}
          aria-label={phase === "speaking" ? "Interrupt and speak" : "Tap to interrupt"}
          style={{
            flexShrink: 0,
            position: "relative", width: 180, height: 180, borderRadius: 90,
            border: "none", padding: 0, cursor: "pointer",
            background:
              "linear-gradient(#0c0d10, #0c0d10) padding-box, " +
              "linear-gradient(90deg, " + phaseColor + "ee, " + phaseColor + "44, " + phaseColor + "ee) border-box",
            borderImage: "none",
            outline: "1px solid " + phaseColor + "55",
            boxShadow: "0 0 60px " + phaseColor + "44, 0 0 0 8px " + phaseColor + "11",
            transition: "transform 0.2s ease, box-shadow 0.4s",
            transform: phase === "listening" ? "scale(" + (1 + micLevel * 0.15) + ")" : phase === "thinking" ? "scale(0.98)" : "scale(1)",
            animation: phase === "thinking" ? "vm-pulse 1.4s ease-in-out infinite" : "none",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {/* Inner mic icon */}
          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: phaseColor, fontSize: 48 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <rect x="9" y="3" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </span>
        </button>

        {/* Phase label (compact) + scrollable content tray that fills the
            remaining vertical space. Long coach replies scroll inside this
            tray instead of being truncated. */}
        <div style={{ width: "100%", textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: phaseColor, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            {phaseLabel}
          </div>
        </div>
        <div
          className="vm-content-scroll"
          style={{
            flex: 1, minHeight: 0, width: "100%",
            maxWidth: 560, margin: "0 auto",
            overflowY: "auto", WebkitOverflowScrolling: "touch",
            padding: "4px 8px 12px",
            color: C.ink, lineHeight: 1.5, textAlign: "center",
            // Slightly smaller body type for long answers so more fits without scrolling.
            fontSize: (phase === "speaking" && (latestReply || "").length > 280) ? 15 : 17,
          }}
        >
          {phase === "listening" && (transcript || "Speak when ready…")}
          {phase === "thinking"  && "Coach is thinking…"}
          {phase === "speaking"  && (latestReply || "Speaking…")}
          {phase === "idle"      && (error || "Tap mic to start")}
        </div>

        {/* Permission CTA */}
        {error && isPermissionError(error) && (
          <button
            onClick={openIOSSettings}
            style={{ padding: "8px 16px", borderRadius: 14, border: "1px solid " + C.coral + "66", background: C.coral + "18", color: C.coral, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Open Settings →
          </button>
        )}
      </div>

      {/* Footer hint — switches text based on phase. Speaking gets the
          stronger "barge-in" framing since voice interrupts work there. */}
      <div style={{ textAlign: "center", padding: "16px 24px 24px", fontSize: 11, color: C.faint, letterSpacing: "0.05em" }}>
        {phase === "speaking"
          ? "Speak or tap the orb to interrupt"
          : phase === "listening"
          ? "Pause when you're done — message sends automatically"
          : "Tap the orb to interrupt at any time"}
      </div>
    </div>,
    document.body
  );
}
